/**
 * Shared actions behind the link/file hover toolbar (desktop) and long-press menu (mobile), see
 * linkFileAffordances.ts. Kept free of any Tauri dependency: copying a file's real bytes to the OS
 * clipboard and saving it to disk are the two operations a browser can't do reliably, so they route
 * through injectable seams (`setFileClipboardWriter` / `setFileSaver`) that the desktop shell overrides
 * with native Tauri commands — exactly like `openExternal`'s `setExternalOpener`. The web defaults do
 * the best a browser allows (image bytes to the clipboard, a `<a download>` for save).
 */
import type { SignedUrlResolver } from './signedUrls.js';
import { openExternal } from '../openExternal.js';

/** A file attachment the affordances act on (mirrors the `file` node's attrs). */
export interface FileTarget {
  /** Stable `lapo://` reference or a direct blob:/data:/https: URL. */
  src: string;
  filename: string;
  contentType: string;
}

/** Args handed to the clipboard writer / saver seams: a fetchable URL plus file metadata. */
export interface FileTransfer {
  url: string;
  filename: string;
  mime: string;
}

export type FileClipboardWriter = (transfer: FileTransfer) => Promise<void>;
export type FileSaver = (transfer: FileTransfer) => Promise<void>;

/** blob:/data:/https: are already fetchable; only `lapo://` refs need the signed-URL resolver. */
const DIRECT_SRC = /^(blob:|data:|https?:)/;

/** Resolve a file's stored reference to a fetchable URL (direct URLs pass through). */
async function resolveFileUrl(src: string, resolveSrc?: SignedUrlResolver): Promise<string> {
  if (DIRECT_SRC.test(src)) return src;
  if (!resolveSrc) throw new Error('no signed-URL resolver');
  return resolveSrc.resolve(src);
}

/**
 * Default (web) file→clipboard: fetch the bytes and put them on the clipboard. Browsers only reliably
 * accept images as a real clipboard payload, so images are normalized to PNG (decoded from the fetched
 * blob, no canvas CORS taint); anything else falls back to copying the file's URL as text so "Copy"
 * always does something useful. The desktop shell replaces this with a native writer that copies real
 * file bytes of any type (see setFileClipboardWriter).
 */
const webClipboardWriter: FileClipboardWriter = async ({ url, mime }) => {
  const blob = await fetch(url).then((r) => r.blob());
  const type = blob.type || mime;
  const clip = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
  if (clip && typeof ClipboardItem !== 'undefined' && type.startsWith('image/')) {
    try {
      const png = type === 'image/png' ? blob : await toPngBlob(blob);
      await clip.write([new ClipboardItem({ 'image/png': png })]);
      return;
    } catch {
      /* fall through to the URL-text fallback */
    }
  }
  // Non-image (or clipboard-image write refused): copy the URL so the user still gets a shareable handle.
  if (clip) await clip.writeText(url);
};

/** Decode an image blob and re-encode it as PNG (the one image type browsers reliably paste). */
async function toPngBlob(blob: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  ctx.drawImage(bitmap, 0, 0);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png');
  });
}

/**
 * Default (web) save: fetch the bytes and trigger a `<a download>` so the browser writes the file to
 * disk with its real name. WKWebView ignores the download attribute, so the desktop shell overrides
 * this with a native saver (see setFileSaver).
 */
const webFileSaver: FileSaver = async ({ url, filename }) => {
  const blob = await fetch(url).then((r) => r.blob());
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename || 'download';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoke after the click has a chance to start the download.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
  }
};

let clipboardWriter: FileClipboardWriter = webClipboardWriter;
let fileSaver: FileSaver = webFileSaver;

/** Host app overrides how a file's bytes reach the OS clipboard (native copy on Tauri). */
export function setFileClipboardWriter(fn: FileClipboardWriter): void {
  clipboardWriter = fn;
}

/** Host app overrides how a file is saved to disk (native save + reveal on Tauri). */
export function setFileSaver(fn: FileSaver): void {
  fileSaver = fn;
}

/** Copy a link's href to the clipboard as text. */
export async function copyLinkHref(href: string): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.clipboard) await navigator.clipboard.writeText(href);
}

/** Open an external link (routes through the shell-aware opener). */
export function openLink(href: string): void {
  openExternal(href);
}

/** Open a file attachment — resolve a fresh URL first so it's never stale, then hand off to the opener. */
export async function openFile(file: FileTarget, resolveSrc?: SignedUrlResolver): Promise<void> {
  openExternal(await resolveFileUrl(file.src, resolveSrc));
}

/** Copy a file's bytes to the OS clipboard (native on desktop, best-effort on web). */
export async function copyFileToClipboard(file: FileTarget, resolveSrc?: SignedUrlResolver): Promise<void> {
  const url = await resolveFileUrl(file.src, resolveSrc);
  await clipboardWriter({ url, filename: file.filename, mime: file.contentType });
}

/** Download a file to disk (native save on desktop, `<a download>` on web). */
export async function downloadFile(file: FileTarget, resolveSrc?: SignedUrlResolver): Promise<void> {
  const url = await resolveFileUrl(file.src, resolveSrc);
  await fileSaver({ url, filename: file.filename, mime: file.contentType });
}
